import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import leftArrow from "../../../../assests/leftArrow.svg";
import { 
  Button, 
  TextField, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel,
  Snackbar,
  Chip,
  Box,
  Typography,
  InputAdornment,
  CircularProgress
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import awaitingicon from "../../../../assests/awaitingIcon.svg"
import "./vmidDashboardDetails.css";

import uidIcon from "../../../../assests/uid.svg";
import vmidIcon from "../../../../assests/vmids.svg";
import calendarIcon from "../../../../assests/calander.svg";
import userIcon from "../../../../assests/user-roles.svg";
import qrIcon from "../../../../assests/qr-icon.svg";
import statusIcon from "../../../../assests/sloticon.svg";
import locationIcon from "../../../../assests/locationIcon.svg";
import dropdownArrow from "../../../../assests/downArrow.png";
import checkIconGreen from "../../../../assests/checkIconGreen.svg";
import searchIcon from "../../../../assests/search.svg";

import QRCodeModal from "./QRCodeModal";

const VmidDashboardDetailsComponent = ({
  uidDetails,
  error,
  navigate,
  onUnmap,
  onMap,
  onConfirmedMap,
  onCancelMap,
  onUidSelect,
  onUidListLoad,
  onUidSearch,
  onVmSearch,
  handleUidTimeline,
  vmList = [],
  uidList = [],
  selectedVmDetails,
  onVmSelect,
  onSuccessNavigation,
  mapSuccess,
  unmapSuccess,
  resetSuccessStates,
  mapConfirmationOpen,
  pendingMapData,
  initialVmInfo,
  searchLoading,
  searchQuery
}) => {
  const { uid } = useParams();
  const [reason, setReason] = useState("");
  const [selectedVmid, setSelectedVmid] = useState("");
  const [selectedUid, setSelectedUid] = useState(uid || "");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [unmapLoading, setUnmapLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  
  const [showQRModal, setShowQRModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Remove isUidSelected state since we want to allow the same UID
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  
  // Validation states
  const [reasonError, setReasonError] = useState("");
  
  // Search states
  const [uidSearchQuery, setUidSearchQuery] = useState("");
  const [vmidSearchQuery, setVmidSearchQuery] = useState("");
  const [uidDropdownOpen, setUidDropdownOpen] = useState(false);
  const [vmidDropdownOpen, setVmidDropdownOpen] = useState(false);

  const uidSearchInputRef = useRef(null);
  const vmidSearchInputRef = useRef(null);

  // Handle initial UID details and selected UID details
  useEffect(() => {
    if (uid && !selectedUid) {
      setSelectedUid(uid);
    }
  }, [uid, selectedUid]);

  useEffect(() => {
    return () => {
      if (resetSuccessStates) {
        resetSuccessStates();
      }
    };
  }, []);

  useEffect(() => {
    if (initialVmInfo?.id) {
      setSelectedVmid(initialVmInfo.id);
    }
  }, [initialVmInfo]);

  // Validate reason function
  const validateReason = (value) => {
    if (!value || value.trim() === "") {
      return "Reason is required";
    }
    if (value.trim().length < 5) {
      return "Reason must be at least 5 characters long";
    }
    if (value.length > 100) {
      return "Reason cannot exceed 100 characters";
    }
    return "";
  };

  // Handle reason change with real-time validation
  const handleReasonChange = (e) => {
    const value = e.target.value;
    setReason(value);
    setReasonError(validateReason(value));
  };

  // Check if reason is valid
  const isReasonValid = () => {
    return reason.trim().length >= 5 && reason.length <= 100 && !validateReason(reason);
  };

  // Get character count for reason
  const getReasonCharacterCount = () => {
    return `${reason.length}/100`;
  };

  // Check if VM info is actually empty or has null values
  const isVmidDropdownEnabled = !initialVmInfo || 
                                !initialVmInfo.id || 
                                initialVmInfo.id === null || 
                                initialVmInfo.id === "null" ||
                                initialVmInfo.id === undefined ||
                                Object.keys(initialVmInfo).length === 0;

  // Get UID info from both API formats
  const getUidInfo = () => {
    if (!uidDetails) return null;
    return uidDetails.uidInfo || uidDetails.data?.uidInfo || uidDetails;
  };

  const getVmInfo = () => {
    if (!uidDetails) return null;
    return uidDetails.vmInfo || uidDetails?.vmInfo || {};
  };

  const currentUidInfo = getUidInfo();
  const currentVmInfo = getVmInfo();

  // Format address function
  const formatAddress = (vmData) => {
    if (vmData?.address) {
      const { address } = vmData;
      if (typeof address === 'string') {
        return address || "N/A";
      }
      if (typeof address === 'object') {
        const { street, city, state } = address;
        const addressParts = [];
        if (street) addressParts.push(street);
        if (city) addressParts.push(city);
        if (state) addressParts.push(state);
        return addressParts.length > 0 ? addressParts.join(', ') : "N/A";
      }
    }
    if (typeof vmData === 'string') {
      return vmData || "N/A";
    }
    if (vmData && typeof vmData === 'object') {
      const possibleAddressKeys = ['address', 'full_address', 'location', 'complete_address'];
      for (const key of possibleAddressKeys) {
        if (vmData[key]) {
          return vmData[key];
        }
      }
      const addressParts = [];
      if (vmData.street) addressParts.push(vmData.street);
      if (vmData.city) addressParts.push(vmData.city);
      if (vmData.state) addressParts.push(vmData.state);
      if (vmData.area) addressParts.push(vmData.area);
      if (vmData.locality) addressParts.push(vmData.locality);
      if (addressParts.length > 0) {
        return addressParts.join(', ');
      }
    }
    return "N/A";
  };

  // Determine what to show in VM card
  const showVmCardDetails = () => {
    if (selectedVmDetails) {
      return 'selected';
    }
    else if (initialVmInfo && initialVmInfo.id) {
      return 'initial';
    }
    else if (currentVmInfo && currentVmInfo.id) {
      return 'current';
    }
    else {
      return 'empty';
    }
  };

  const vmCardContentType = showVmCardDetails();

  // UID SELECTION WITH SEARCH
  const handleUidSelect = (event) => {
    const selectedValue = event.target.value;
    setSelectedUid(selectedValue);
    setHasUserInteracted(true);
    if (onUidSelect) {
      onUidSelect(selectedValue);
    }
  };

  // VMID SELECTION
  const handleVmidSelect = (event) => {
    const selectedValue = event.target.value;
    setSelectedVmid(selectedValue);
    setHasUserInteracted(true);
    if (onVmSelect) {
      onVmSelect(selectedValue);
    }
  };

  // Check if mapping is allowed
  const isMappingAllowed = () => {
    if (hasUserInteracted && selectedVmid && selectedUid) {
      return true;
    }
    if (!hasUserInteracted && selectedVmid && selectedUid) {
      return true;
    }
    return false;
  };

  // UID DROPDOWN HANDLERS
  const handleUidDropdownOpen = () => {
    setUidDropdownOpen(true);
    if (onUidListLoad) {
      onUidListLoad();
    }
    setUidSearchQuery("");
  };

  const handleUidDropdownClose = () => {
    setUidDropdownOpen(false);
    setUidSearchQuery("");
  };

  // VMID DROPDOWN HANDLERS
  const handleVmidDropdownOpen = () => {
    setVmidDropdownOpen(true);
    setVmidSearchQuery("");
  };

  const handleVmidDropdownClose = () => {
    setVmidDropdownOpen(false);
    setVmidSearchQuery("");
  };

  // SEARCH HANDLERS
  const handleUidSearchChange = (e) => {
    const query = e.target.value;
    setUidSearchQuery(query);
    if (onUidSearch) {
      onUidSearch(query);
    }
  };

  const handleVmidSearchChange = (e) => {
    const query = e.target.value;
    setVmidSearchQuery(query);
    if (onVmSearch) {
      onVmSearch(query);
    }
  };

  const handleSearchFieldClick = (e) => {
    e.stopPropagation();
  };

  const handleSearchKeyDown = (e) => {
    e.stopPropagation();
  };

  const mappingState = currentUidInfo?.mappingState;
  const isMapped = mappingState === "Mapped";
  const isUnmapped = mappingState === "Unmapped";

  const showDropdowns = isUnmapped;
  const showUidDetails = selectedUid && currentUidInfo;

  // Format date function
  const formatDashboardDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const istDate = moment.utc(dateString).utcOffset('+05:30');
      return istDate.format('MMMM Do, YYYY [at] hh:mm A ');
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const getFormattedDate = (dateObj, fallback = "N/A") => {
    if (!dateObj) return fallback;
    if (typeof dateObj === 'string') {
      return formatDashboardDate(dateObj);
    }
    if (dateObj.date) {
      return formatDashboardDate(dateObj.date);
    }
    return fallback;
  };

  const showSuccess = mapSuccess || unmapSuccess;

  const handleDashboardUnmapClick = (e) => {
    e.preventDefault();
    
    if (mappingState === "Unmapped") {
      alert("This UID is already unmapped!");
      return;
    }

    const validationError = validateReason(reason);
    if (validationError) {
      setReasonError(validationError);
      alert(validationError);
      return;
    }

    if (!isReasonValid()) {
      alert("Please enter a valid reason (5-100 characters)");
      return;
    }

    setShowConfirmation(true);
  };

  const handleDashboardMapClick = (e) => {
    e.preventDefault();
    
    if (!selectedVmid) {
      alert("Please select a VMID");
      return;
    }

    if (!selectedUid) {
      alert("Please select a UID");
      return;
    }

    if (onMap) {
      onMap(selectedVmid, reason || "Mapping UID to VM");
    }
  };

  const handleDashboardConfirmMap = async () => {
    setMapLoading(true);
    try {
      if (onConfirmedMap) {
        await onConfirmedMap();
      }
    } catch (error) {
      console.error("Map error:", error);
    } finally {
      setMapLoading(false);
    }
  };

  const handleDashboardCancelMap = () => {
    if (onCancelMap) {
      onCancelMap();
    }
  };

  const handleDashboardConfirmUnmap = async () => {
    setShowConfirmation(false);
    setUnmapLoading(true);

    try {
      if (onUnmap) {
        await onUnmap(reason);
      }
    } catch (error) {
      console.error("Unmap error:", error);
    } finally {
      setUnmapLoading(false);
    }
  };

  const handleDashboardCancelUnmap = () => {
    setShowConfirmation(false);
  };

  const handleDashboardSuccessOk = () => {
    if (onSuccessNavigation) {
      onSuccessNavigation();
    }
  };

  // UPDATED: Only open modal, NO image download
  const handleDashboardQRClick = async (e) => {
    e.preventDefault();
    
    if (!selectedUid) {
      alert("Please select a UID first");
      return;
    }
    
    // Just open the modal - PDF will auto-download from QRCodeModal
    setShowQRModal(true);
  };

  const handleDashboardQRClose = () => {
    setShowQRModal(false);
  };

  const handleDashboardSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // RENDER FUNCTIONS
  const renderUidMenuItem = (uidItem) => {
    return (
      <MenuItem key={uidItem.uid} value={uidItem.uid}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={uidIcon} alt="UID" style={{ width: 16, height: 16, opacity: 0.7 }} />
            <Typography variant="body2" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
              {uidItem.uid}
            </Typography>
          </Box>
          <Chip 
            label={uidItem.mappingState || "Unknown"} 
            size="small"
            color={
              uidItem.mappingState === "Mapped" ? "success" :
              uidItem.mappingState === "Unmapped" ? "warning" : "default"
            }
            variant={uidItem.mappingState === "Mapped" ? "filled" : "outlined"}
            sx={{ 
              fontSize: '0.7rem', 
              height: 20,
              minWidth: 70,
              '& .MuiChip-label': { px: 1 }
            }}
          />
        </Box>
      </MenuItem>
    );
  };

  const renderVmidMenuItem = (vmItem) => {
    return (
      <MenuItem key={vmItem.id} value={vmItem.id}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={vmidIcon} alt="VMID" style={{ width: 16, height: 16, opacity: 0.7 }} />
            <Box sx={{display:"flex", gap:"6px"}}>
              <Typography variant="body2" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
                {vmItem.vmId || vmItem.id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {vmItem.name}
              </Typography>
            </Box>
          </Box>
          <Chip 
            label="Active" 
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>
      </MenuItem>
    );
  };

  const getVmDataForCard = () => {
    switch (vmCardContentType) {
      case 'selected':
        return selectedVmDetails;
      case 'initial':
        return initialVmInfo;
      case 'current':
        return currentVmInfo;
      default:
        return null;
    }
  };

  const vmData = getVmDataForCard();

  return (
    <div className="uid-unmap-container">
      <Sidebar />
      <div className="uid-unmap-content">
        <div className="uid-unmap-header">
          <div style={{ display: "flex", justifyContent: "center", gap: "10px", alignItems: "center" }}>
            <Button className="back-btn" onClick={() => navigate(-1)}>
              <img src={leftArrow} alt="Back" />
            </Button>
            <h1 className="page-title">
              {isMapped ? "Unmapping UID - VMID" : "Map UIDs-VMIDs"}
            </h1>
          </div>
        </div>

        {showDropdowns && (
          <div className="selection-sections-container">
            {/* UID SELECTION */}
            <div className="selection-section">
              <div className="selection-container">
                <FormControl fullWidth variant="standard">
                  <InputLabel>Select UID</InputLabel>
                  <Select
                    value={selectedUid}
                    onChange={handleUidSelect}
                    onOpen={handleUidDropdownOpen}
                    onClose={handleUidDropdownClose}
                    open={uidDropdownOpen}
                    label="Select UID"
                    IconComponent={() => <img src={dropdownArrow} alt="▼" className="dropdown-icon" />}
                    displayEmpty
                    MenuProps={{
                      disableAutoFocusItem: true,
                      PaperProps: {
                        style: {
                          maxHeight: 450,
                        },
                      },
                    }}
                  >
                    {uidDropdownOpen && (
                      <div 
                        className="search-field-container" 
                        onClick={handleSearchFieldClick}
                        onKeyDown={handleSearchKeyDown}
                      >
                        <TextField
                          fullWidth
                          variant="standard"
                          placeholder="Search by UID..."
                          value={uidSearchQuery}
                          onChange={handleUidSearchChange}
                          inputRef={uidSearchInputRef}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <img src={searchIcon} alt="Search" className="search-icon" />
                              </InputAdornment>
                            ),
                            disableUnderline: true,
                          }}
                          autoFocus
                          onKeyDown={handleSearchKeyDown}
                          onClick={handleSearchFieldClick}
                        />
                        {searchLoading && (
                          <div style={{ padding: '8px', textAlign: 'center' }}>
                            <CircularProgress size={20} />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {uidList && uidList.length > 0 ? (
                      uidList.map((uidItem) => renderUidMenuItem(uidItem))
                    ) : (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {uidSearchQuery ? "No UIDs found for your search" : "No UIDs available"}
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </div>
            </div>

            {/* VMID SELECTION */}
            <div className="selection-section">
              <div className="selection-container">
                <FormControl fullWidth variant="standard">
                  <InputLabel>Select VMID</InputLabel>
                  <Select
                    value={selectedVmid}
                    onChange={handleVmidSelect}
                    onOpen={handleVmidDropdownOpen}
                    onClose={handleVmidDropdownClose}
                    open={vmidDropdownOpen}
                    label="Select VMID"
                    IconComponent={() => <img src={dropdownArrow} alt="▼" className="dropdown-icon" />}
                    displayEmpty
                    disabled={!isVmidDropdownEnabled}
                    MenuProps={{
                      disableAutoFocusItem: true,
                      PaperProps: {
                        style: {
                          maxHeight: 450,
                        },
                      },
                    }}
                  >
                    {isVmidDropdownEnabled && vmidDropdownOpen && (
                      <div 
                        className="search-field-container" 
                        onClick={handleSearchFieldClick}
                        onKeyDown={handleSearchKeyDown}
                      >
                        <TextField
                          fullWidth
                          variant="standard"
                          placeholder="Search by VMID or Name..."
                          value={vmidSearchQuery}
                          onChange={handleVmidSearchChange}
                          inputRef={vmidSearchInputRef}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <img src={searchIcon} alt="Search" className="search-icon" />
                              </InputAdornment>
                            ),
                            disableUnderline: true,
                          }}
                          autoFocus
                          onKeyDown={handleSearchKeyDown}
                          onClick={handleSearchFieldClick}
                        />
                        {searchLoading && (
                          <div style={{ padding: '8px', textAlign: 'center' }}>
                            <CircularProgress size={20} />
                          </div>
                        )}
                      </div>
                    )}
                    
                    {isVmidDropdownEnabled ? (
                      vmList && vmList.length > 0 ? (
                        vmList.map((vmItem) => renderVmidMenuItem(vmItem))
                      ) : (
                        <MenuItem disabled>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {vmidSearchQuery ? "No VMIDs found for your search" : "No VMIDs available"}
                          </Typography>
                        </MenuItem>
                      )
                    ) : initialVmInfo && initialVmInfo.id ? (
                      <MenuItem value={initialVmInfo.id} disabled>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                          <img src={vmidIcon} alt="VMID" style={{ width: 16, height: 16, opacity: 0.7 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'medium', fontFamily: 'monospace' }}>
                            {initialVmInfo.id}
                          </Typography>
                          <Chip 
                            label="Active" 
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20, ml: 'auto' }}
                          />
                        </Box>
                      </MenuItem>
                    ) : (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No VMID available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
        )}

        <div className="uid-vmid-section">
          {/* UID CARD */}
          <div className="card uid-card">
            <div className="card-header">
              <div className="card-title-with-icon">
                <img src={uidIcon} alt="UID" className="card-title-icon" />
                <h2>
                  {currentUidInfo ? `UID: ${currentUidInfo.uid}` : "UID Information"}
                  {mappingState && (
                    <Chip 
                      label={mappingState} 
                      size="small"
                      color={mappingState === "Mapped" ? "success" : mappingState === "Unmapped" ? "warning" : "default"}
                      sx={{ ml: 2, fontSize: '0.75rem' }}
                    />
                  )}
                </h2>
              </div>
            </div>

            {showUidDetails && currentUidInfo ? (
              <>
                <div className="uid-card-detail-item">
                  <img src={calendarIcon} alt="Created" className="detail-icon" />
                  <p><strong>Created On:</strong> {getFormattedDate(currentUidInfo.createdAt)}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={calendarIcon} alt="Updated" className="detail-icon" />
                  <p><strong>Last Mapped:</strong> {getFormattedDate(currentUidInfo.lastUpdatedAt)}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={userIcon} alt="Admin" className="detail-icon" />
                  <p><strong>Admin Name:</strong> {currentUidInfo.createdByName || "N/A"}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={qrIcon} alt="QR" className="detail-icon" />
                  <p>
                    <strong>QR:</strong>{" "}
                    <a 
                      href="#" 
                      onClick={handleDashboardQRClick}
                      style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      {`QR_${selectedUid}`}
                    </a>
                  </p>
                </div>
              </>
            ) : (
              <div className="no-selection-message">
                <img src={uidIcon} alt="UID" className="no-selection-icon" />
                <p><strong>Select a UID to view details</strong></p>
                <p style={{color: '#666', fontSize: '14px'}}>Choose a UID from the dropdown above</p>
              </div>
            )}
          </div>

          {/* VMID CARD */}
          <div className="card vmid-card">
            <div className="card-header">
              <div className="card-title-with-icon">
                <img src={vmidIcon} alt="VMID" className="card-title-icon" />
                <h2>
                  {vmCardContentType === 'selected' ? (
                    `VMID: ${selectedVmDetails.id}`
                  ) : vmCardContentType === 'initial' ? (
                    `VMID: ${initialVmInfo.id}`
                  ) : vmCardContentType === 'current' ? (
                    `VMID: ${currentVmInfo.id}`
                  ) : (
                    "VM Information"
                  )}
                </h2>
              </div>
            </div>

            {vmData ? (
              <>
                <div className="uid-card-detail-item">
                  <img src={userIcon} alt="Name" className="detail-icon" />
                  <p><strong>Name:</strong> {vmData.name || "N/A"}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={calendarIcon} alt="Created" className="detail-icon" />
                  <p><strong>Created On:</strong> {getFormattedDate(vmData.createdAt || vmData.created_at)}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={locationIcon} alt="Address" className="detail-icon" />
                  <p><strong>Address:</strong> {formatAddress(vmData)}</p>
                </div>
                
                <div className="uid-card-detail-item">
                  <img src={userIcon} alt="Admin" className="detail-icon" />
                  <p><strong>Admin Name:</strong> {vmData.createdByName || vmData.admin_name || "N/A"}</p>
                </div>
              </>
            ) : (
              <div className="no-selection-message">
                <img src={vmidIcon} alt="VMID" className="no-selection-icon" />
                <p><strong>No VM information available</strong></p>
              </div>
            )}
          </div>
        </div>

        {isMapped && (
          <div className="reason-box">
            <h3>Reason For Unmapping</h3>
            <TextField
              fullWidth
              value={reason}
              onChange={handleReasonChange}
              variant="standard"
              placeholder="Enter reason for unmapping (5-100 characters)"
              error={!!reasonError}
              helperText={reasonError || getReasonCharacterCount()}
              inputProps={{
                maxLength: 100
              }}
            />
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                display: 'block', 
                mt: 0.5,
                fontSize: '0.75rem',
                color: reason.length > 100 ? 'error.main' : 'text.secondary'
              }}
            >
              {getReasonCharacterCount()} characters
            </Typography>
          </div>
        )}

        <div className="action-btn-section">
          {isMapped ? (
            <Button
              variant="contained"
              className="unmap-btn"
              onClick={handleDashboardUnmapClick}
              disabled={unmapLoading || !isReasonValid()}
              color="warning"
            >
              {unmapLoading ? "Unmapping..." : "Unmap Data"}
            </Button>
          ) : (
            <Button
              variant="contained"
              className="map-btn"
              onClick={handleDashboardMapClick}
              disabled={mapLoading || !isMappingAllowed()}
              color="primary"
            >
              {mapLoading ? "Mapping..." : "Map Data"}
            </Button>
          )}
        </div>

        {/* QR CODE MODAL - Only PDF will download */}
        <QRCodeModal
          open={showQRModal}
          onClose={handleDashboardQRClose}
          uid={selectedUid}
        />

        {showConfirmation && isMapped && (
          <div className="popup-overlay">
            <div className="confirmation-popup">
              <img src={awaitingicon} alt="" style={{ height: "80px", width: "80px", marginBottom: "10px" }} />
              <h3>Are You Sure You Want To Unmap</h3>
              <p> {currentUidInfo?.uid} - {initialVmInfo?.id || currentVmInfo?.id} ?</p>
              <div className="confirmation-buttons">
                <Button className="cancel-btn" onClick={handleDashboardCancelUnmap}>
                  No
                </Button>
                <Button className="confirm-btn" onClick={handleDashboardConfirmUnmap}>
                  Yes
                </Button>
              </div>
            </div>
          </div>
        )}

        {mapConfirmationOpen && (
          <div className="popup-overlay">
            <div className="confirmation-popup">
              <img src={awaitingicon} alt="" style={{ height: "80px", width: "80px", marginBottom: "10px" }} />
              <h3>Are You Sure You Want To Map</h3>
              <p>{selectedUid} to VMID {pendingMapData?.vmid}?</p>
              <div className="confirmation-buttons">
                <Button className="cancel-btn" onClick={handleDashboardCancelMap} disabled={mapLoading}>
                  No
                </Button>
                <Button 
                  className="confirm-btn" 
                  onClick={handleDashboardConfirmMap}
                  disabled={mapLoading}
                >
                  {mapLoading ? "Mapping..." : "Yes"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {showSuccess && (
          <div className="popup-overlay">
            <div className="success-popup-vm">
              <div className="success-header-vm">
                <div className="success-icon-vm"><img src={checkIconGreen} alt="" style={{ height: "80px", width: "80px", marginBottom: "10px" }} /></div>
                <h3>Successful</h3>
              </div>
              <div className="success-content">
                <div className="success-message">
                  <p>
                    {unmapSuccess
                      ? `${currentUidInfo?.uid} - ${initialVmInfo?.id || currentVmInfo?.id} Unmapped Successfully`
                      : `${selectedUid} mapped to VMID ${pendingMapData?.vmid} Successfully`
                    }
                  </p>
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Button className="success-details-ok-btn" onClick={handleDashboardSuccessOk}>
                  Ok
                </Button>
              </div>
            </div>
          </div>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleDashboardSnackbarClose}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </div>
    </div>
  );
};

export default VmidDashboardDetailsComponent;